import { useEffect, useState, useCallback } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { messaging, VAPID_KEY } from '../lib/firebase';
import { supabase } from '../lib/supabase';

export function usePushNotifications(teamId: string | undefined) {
  const [permissionState, setPermissionState] = useState<NotificationPermission>('default');
  const [fcmReady, setFcmReady] = useState(false);
  const [fcmError, setFcmError] = useState<string | null>(null);
  const [fcmStep, setFcmStep] = useState<string>('idle');

  const registerAndSubscribe = useCallback(async () => {
    if (!teamId) return;
    try {
      setFcmStep('checking-support');
      if (!('Notification' in window) || !('serviceWorker' in navigator)) {
        setFcmError('Notifications non supportées sur ce navigateur');
        return;
      }

      setFcmStep('checking-permission');
      const permission = Notification.permission;
      setPermissionState(permission);

      if (permission === 'denied') {
        setFcmError('Notifications bloquées — activez-les dans les paramètres du navigateur');
        return;
      }

      setFcmStep('registering-sw');
      const existingRegs = await navigator.serviceWorker.getRegistrations();
      for (const reg of existingRegs) {
        if (reg.scope.includes(window.location.origin)) await reg.update();
      }
      const sw = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
      await sw.update();
      await navigator.serviceWorker.ready;

      setFcmStep('requesting-permission');
      let finalPermission: NotificationPermission = permission;
      if (permission === 'default') {
        finalPermission = await Notification.requestPermission();
        setPermissionState(finalPermission);
      }

      if (finalPermission !== 'granted') {
        setFcmError('Permission refusée');
        setFcmStep('permission-denied');
        return;
      }

      setFcmStep('getting-token');

      if (!messaging) {
        setFcmError('Firebase Messaging non supporté sur ce navigateur/appareil');
        setFcmStep('error-unsupported');
        return;
      }

      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: sw
      });

      if (token) {
        setFcmStep('saving-token');
        const { error } = await supabase.rpc('save_fcm_token', { p_staff_id: teamId, p_token: token });
        if (error) {
          setFcmError('Erreur RPC: ' + error.message);
          setFcmStep('error-rpc');
          return;
        }
        setFcmReady(true);
        setFcmStep('ready');
        setFcmError(null);
      } else {
        setFcmError('Token vide — vérifiez config Firebase');
        setFcmStep('error-empty-token');
      }
    } catch (err: any) {
      const msg = err?.message || String(err);
      setFcmError(msg);
      setFcmStep('error');
      console.error('[FCM] Erreur:', err);
    }
  }, [teamId]);

  useEffect(() => {
    registerAndSubscribe();

    const unsubscribe = messaging
      ? onMessage(messaging, (payload) => {
          const title = payload.notification?.title || 'Bip Teams';
          const body = payload.notification?.body || 'Nouveau message';
          if (Notification.permission === 'granted') {
            new Notification(title, { body, icon: '/icons/icon-512.png', tag: 'bip-message' });
          }
        })
      : () => {};

    return () => unsubscribe();
  }, [registerAndSubscribe]);

  // Appelé par le bouton Bell ou le bouton dans le debug panel
  const requestPermission = useCallback(() => {
    setFcmReady(false);
    setFcmError(null);
    setFcmStep('idle');
    registerAndSubscribe();
  }, [registerAndSubscribe]);

  return { permissionState, fcmReady, fcmError, fcmStep, requestPermission };
}
